import { Text, XStack, YStack } from '@/components/ui';

import type {
  GraphEntity,
  GraphRelationship,
  GraphSelection,
} from './world-model.types';

function toTitleCase(text: string): string {
  return text.replace(/\b\w/g, (char) => char.toUpperCase());
}

function timeAgo(isoTimestamp: string): string {
  const diffMs = Date.now() - new Date(isoTimestamp).getTime();
  const diffMinutes = Math.floor(diffMs / 60_000);
  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(isoTimestamp).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year:
      new Date(isoTimestamp).getFullYear() !== new Date().getFullYear()
        ? 'numeric'
        : undefined,
  });
}

function EntityTypeBadge({ type, color }: { type: string; color: string }) {
  return (
    <span
      className="inline-block w-fit px-2 py-0.5 rounded text-xs font-medium"
      style={{
        backgroundColor: color + '22',
        border: `1px solid ${color}55`,
        color,
      }}
    >
      {type}
    </span>
  );
}

function NodeDetail({
  entity,
  color,
  onClose,
}: {
  entity: GraphEntity;
  color: string;
  onClose: () => void;
}) {
  const props = Object.entries(entity.properties ?? {}).filter(
    ([, v]) => v !== null && v !== undefined && v !== ''
  );

  return (
    <YStack className="gap-3">
      <XStack className="items-start justify-between gap-2">
        {entity.name && (
          <Text className="font-semibold text-sm leading-snug">
            {toTitleCase(entity.name)}
          </Text>
        )}
        <button
          onClick={onClose}
          className="text-foreground-muted hover:text-foreground text-sm leading-none shrink-0 mt-0.5"
          aria-label="Close"
        >
          ✕
        </button>
      </XStack>

      <EntityTypeBadge type={entity.type} color={color} />

      {entity.content && (
        <div>
          <Text size="xs" lowContrast className="mb-1 uppercase tracking-wide">
            Content
          </Text>
          <Text size="sm" className="text-foreground-subtle line-clamp-4">
            {entity.content}
          </Text>
        </div>
      )}

      {props.length > 0 && (
        <div>
          <Text size="xs" lowContrast className="mb-1 uppercase tracking-wide">
            Properties
          </Text>
          <YStack className="gap-1">
            {props.map(([k, v]) => (
              <XStack key={k} className="gap-1">
                <Text size="sm" lowContrast className="shrink-0 capitalize">
                  {k.replace(/_/g, ' ')}:
                </Text>
                <Text size="sm" className="text-foreground-subtle break-all">
                  {String(v)}
                </Text>
              </XStack>
            ))}
          </YStack>
        </div>
      )}
    </YStack>
  );
}

function EdgeDetail({
  rel,
  onClose,
}: {
  rel: GraphRelationship;
  onClose: () => void;
}) {
  const originalContext = rel.context.filter(
    (c) => c.type === 'original_context'
  );
  const updates = rel.context
    .filter((c) => c.type === 'update')
    .sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

  return (
    <YStack className="gap-3">
      <XStack className="items-start justify-between gap-2">
        <Text size="xs" lowContrast className="uppercase tracking-wide mt-0.5">
          Relationship
        </Text>
        <button
          onClick={onClose}
          className="text-foreground-muted hover:text-foreground text-sm leading-none shrink-0"
          aria-label="Close"
        >
          ✕
        </button>
      </XStack>

      <YStack className="gap-1">
        <XStack className="items-center gap-1 flex-wrap">
          <Text size="sm" className="font-medium">
            {toTitleCase(rel.source_name ?? rel.source)}
          </Text>
          <Text size="sm" lowContrast>
            →
          </Text>
          <Text size="sm" className="font-medium">
            {toTitleCase(rel.target_name ?? rel.target)}
          </Text>
        </XStack>
        {rel.semantic && (
          <Text size="xs" lowContrast className="font-mono">
            {rel.semantic}
          </Text>
        )}
      </YStack>

      {originalContext.length > 0 && (
        <YStack className="gap-1.5">
          <Text size="xs" lowContrast className="uppercase tracking-wide">
            Context
          </Text>
          <YStack className="gap-2">
            {originalContext.map((c, i) => (
              <Text
                key={i}
                size="sm"
                className="text-foreground-subtle leading-snug"
              >
                {c.text}
              </Text>
            ))}
          </YStack>
        </YStack>
      )}

      {updates.length > 0 && (
        <YStack className="gap-1.5">
          <Text size="xs" lowContrast className="uppercase tracking-wide">
            Updates
          </Text>
          <YStack className="gap-3">
            {updates.map((update, i) => (
              <YStack key={i} className="gap-0.5">
                <Text size="sm" className="text-foreground-subtle leading-snug">
                  {update.text}
                </Text>
                <Text size="xs" lowContrast>
                  {timeAgo(update.timestamp)}
                </Text>
              </YStack>
            ))}
          </YStack>
        </YStack>
      )}
    </YStack>
  );
}

export function GraphDetailPanel({
  selection,
  onClose,
}: {
  selection: GraphSelection;
  onClose: () => void;
}) {
  if (!selection) return null;

  return (
    <div className="absolute bottom-8 right-8 w-fit min-w-[260px] max-w-[480px] rounded-lg border border-border bg-surface shadow-xl overflow-y-auto max-h-80 p-5">
      {selection.kind === 'node' ? (
        <NodeDetail
          entity={selection.entity}
          color={selection.color}
          onClose={onClose}
        />
      ) : (
        <EdgeDetail rel={selection.rel} onClose={onClose} />
      )}
    </div>
  );
}
